import React from 'react'
import { createContext, useContext } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { setIdentity } from 'redux/actions'
import { ReactReduxContext } from 'react-redux'
import Store from 'redux/Store'
import { setCustomData, setSettings } from 'shared/redux/actions'
import AccessDenied from '../AccessDenied'

interface AuthContextType {
  signIn: (
    username: string,
    token: string,
    customData: {} | null,
    settings: {} | null,
    callback: VoidFunction,
  ) => boolean
  signOut: (callback: VoidFunction) => boolean
}

let AuthContext = createContext<AuthContextType>(null!)

function AuthProvider({ children }: { children: React.ReactNode }) {
  let signIn = (
    username: string,
    token: string,
    customData: {} | null,
    settings: {} | null,
    callback: VoidFunction,
  ) => {
    if (Store.dispatch(setIdentity(username, token))) {
      Store.dispatch(setCustomData(customData))
      Store.dispatch(setSettings(settings))
      callback()
      return true
    } else {
      return false
    }
  }

  let signOut = (callback: VoidFunction) => {
    Store.dispatch(setIdentity('', false))
    Store.dispatch(setCustomData(null))
    Store.dispatch(setSettings(null))
    callback()
    return true
  }

  let value = { signIn, signOut }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function useAuth() {
  return useContext(AuthContext)
}

function RequireAuth({ children, roles }: { children: JSX.Element; roles: String[] | undefined }) {
  let location = useLocation()
  const { store } = useContext(ReactReduxContext)
  let identity = store.getState().identity
  const userRole = store.getState().customData?.customData?.role
  if (identity.username !== '' && identity.token !== false) {
    return roles === undefined || roles.includes(userRole) ? children : <AccessDenied />
  } else {
    return <Navigate to='/login' state={{ from: location }} replace />
  }
}

function getToken() {
  return Store.getState().identity.token
}

function getCustomData(key: string) {
  const customData = Store.getState().customData?.customData
  return customData?.hasOwnProperty(key) ? customData[key] : null
}

function getSettingsValue(key: string) {
  const settings = Store.getState().settings
  return settings?.hasOwnProperty(key) ? settings[key] : null
}

function setSettingsValue(key: string, value: any) {
  Store.dispatch(setSettings({ [key]: value }))
}

function getAllCustomData() {
  return Store.getState().customData?.customData
}

export {
  AuthContext,
  AuthProvider,
  useAuth,
  RequireAuth,
  getToken,
  getCustomData,
  getSettingsValue,
  getAllCustomData,
  setSettingsValue,
}
